import React from 'react'
import { DollarSign, Percent, Edit, Trash2, AlertCircle, CheckCircle } from 'lucide-react'
import { FinancialCharge } from '../../types'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Button, Badge } from '../ui'
import { formatCurrency } from '../../utils/formatters'

interface FinancialChargesListViewProps {
 charges: FinancialCharge[]
 onEditCharge: (charge: FinancialCharge) => void
 onDeleteCharge: (id: string) => void
 onViewCharge?: (charge: FinancialCharge) => void
}

export const FinancialChargesListView: React.FC<FinancialChargesListViewProps> = ({
 charges,
 onEditCharge,
 onDeleteCharge,
 onViewCharge
}) => {
 const getTypeLabel = (type: string) => {
 const types: Record<string, string> = {
 transfer: 'Transferência',
 tax: 'Imposto',
 fee: 'Taxa',
 warranty: 'Garantia',
 insurance: 'Seguro',
 ipva: 'IPVA',
 licensing: 'Licenciamento',
 documentation: 'Documentação',
 inspection: 'Vistoria',
 maintenance: 'Revisão/Manutenção',
 accessories: 'Acessórios',
 financing: 'Financiamento',
 transfer_fee: 'Taxa de Transferência',
 other: 'Outro'
 }
 return types[type] || type
 }

 const getTypeColor = (type: string) => {
 const colors: Record<string, string> = {
 transfer: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
 tax: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
 fee: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
 warranty: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
 insurance: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
 ipva: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
 licensing: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
 documentation: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
 inspection: 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200',
 maintenance: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
 accessories: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
 financing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
 transfer_fee: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
 other: 'bg-surface text-foreground dark:bg-background dark:text-muted-foreground'
 }
 return colors[type] || colors.other
 }

 const formatValue = (charge: FinancialCharge) => {
 if (charge.isPercentage) {
 return `${charge.percentage}%`
 }
 return formatCurrency(charge.amount)
 }

 if (charges.length === 0) {
 return (
 <div className="text-center py-12">
 <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4"/>
 <h3 className="text-lg font-medium text-foreground mb-2">
 Nenhum encargo encontrado
 </h3>
 <p className="text-muted-foreground">
 Crie seu primeiro encargo financeiro para começar.
 </p>
 </div>
 )
 }

 return (
 <div className="overflow-x-auto">
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>Nome</TableHead>
 <TableHead>Tipo</TableHead>
 <TableHead>Valor</TableHead>
 <TableHead>Status</TableHead>
 <TableHead className="text-right">Ações</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {charges.map((charge) => (
 <TableRow key={charge.id}>
 <TableCell>
 <div>
 <p className="font-medium text-foreground">
 {charge.name}
 </p>
 {charge.description && (
 <p className="text-sm text-muted-foreground truncate max-w-xs">
 {charge.description}
 </p>
 )}
 </div>
 </TableCell>
 
 <TableCell>
 <Badge className={getTypeColor(charge.type)}>
 {getTypeLabel(charge.type)}
 </Badge>
 </TableCell>
 
 <TableCell>
 <div className="flex items-center gap-1">
 {charge.isPercentage ? (
 <Percent className="h-4 w-4 text-muted-foreground"/>
 ) : (
 <DollarSign className="h-4 w-4 text-muted-foreground"/>
 )}
 <span className="font-medium">
 {formatValue(charge)}
 </span>
 </div>
 </TableCell>
 
 <TableCell>
 <div className="flex items-center gap-2">
 {charge.isActive ? (
 <CheckCircle className="h-4 w-4 text-green-500"/>
 ) : (
 <AlertCircle className="h-4 w-4 text-muted-foreground"/>
 )}
 <span className={`text-sm ${charge.isActive ? 'text-green-600' : 'text-muted-foreground'}`}>
 {charge.isActive ? 'Ativo' : 'Inativo'}
 </span>
 {charge.isRequired && (
 <Badge className="text-xs bg-surface text-foreground dark:bg-surface dark:text-muted-foreground">
 Obrigatório
 </Badge>
 )}
 </div>
 </TableCell>
 
 <TableCell className="text-right">
 <div className="flex items-center justify-end gap-2">
 {onViewCharge && (
 <Button
 variant="outline"
 size="sm"
 onClick={() => onViewCharge(charge)}
 >
 Ver
 </Button>
 )}
 <Button
 variant="outline"
 size="sm"
 onClick={() => onEditCharge(charge)}
 >
 <Edit className="h-4 w-4"/>
 </Button>
 <Button
 variant="outline"
 size="sm"
 onClick={() => onDeleteCharge(charge.id)}
 className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
 >
 <Trash2 className="h-4 w-4"/>
 </Button>
 </div>
 </TableCell>
 </TableRow>
 ))}
 </TableBody>
 </Table>
 </div>
 )
}
